import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'

const DEBUG_MODE = process.env.SENTRY_DEBUG === 'true'

if (DEBUG_MODE || (process.env.NODE_ENV === 'production' && process.env.BUILD_ENV === 'CI' && process.env.APP_ENV && process.env.SENTRY_DSN && process.env.GIT_COMMIT_HASH)) {
  try {
    type SentryHijackXMLHttpRequest = XMLHttpRequest & {
      __sentry_headers__?: Record<string, string>
    }
    const noConflitSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader
    XMLHttpRequest.prototype.setRequestHeader = function (this: SentryHijackXMLHttpRequest, name, value) {
      const response = noConflitSetRequestHeader.call(this, name, value)
      if (typeof this.__sentry_headers__ !== 'object') {
        this.__sentry_headers__ = {}
      }

      this.__sentry_headers__[name] = value
      return response
    }

    Sentry.init({
      /** 告诉 SDK 上报错误/异常的地方 */
      /** https://sentry-new.myshopline.com/settings/logistics-nk/projects/onewarehouse-admin/keys/ */
      dsn: process.env.SENTRY_DSN || '',
      /** 开启 DEBUG 模式 */
      debug: DEBUG_MODE && process.env.APP_ENV !== 'product',
      /** 代码的版本号 Release 版本号, 可以确定当前的错误/异常属于哪一个发布的版本 */
      release: `release@${process.env.GIT_COMMIT_HASH}`,
      /** 上报当前错误/异常发生的环境 */
      environment: process.env.APP_ENV,
      /** 是否记录session */
      autoSessionTracking: false,
      /** Sentry集成 */
      integrations: [new Integrations.BrowserTracing()],
      /** 需要过滤的错误类型，具体可以参考 [Filtering and Sampling Events](https://docs.sentry.io/platforms/javascript/configuration/filtering/) */
      /** 过时的 api、浏览器干预等报告等的打印, Promise，字符串匹配实现 */
      ignoreErrors: ['ReportingObserver [deprecation]', 'Uncaught (in promise)'],
      /** 黑名单 */
      blacklistUrls: [/^localhost/, /extensions\//i, /^chrome:\/\//i, /^chrome-extensions:\/\//i, /^file:\/\//i],
      /** 随机上报事件的概率 */
      tracesSampleRate: 0,
      /** 面包屑注入 */
      beforeBreadcrumb(breadcrumb, hint) {
        switch (breadcrumb.category) {
          // 过滤 CONSOLE LOG WARN
          case 'console': {
            if (hint.level !== 'error') {
              return null
            }
            break
          }

          // XHR 添加数据
          case 'xhr': {
            // 设置 traceid
            const xhr: SentryHijackXMLHttpRequest = hint.xhr
            const { traceparent: traceId } = xhr.__sentry_headers__ || {}

            if (traceId) {
              breadcrumb.data = Object.assign(breadcrumb.data || {}, {
                traceId: traceId.split('-')[1],
              })
            }

            break
          }

          // 用户操作
          case 'ui.click': {
            // 注入按钮文本
            const { event } = hint
            const target: HTMLElement = event.target
            const content = target.innerText || ''
            if (content) {
              breadcrumb.data = Object.assign(breadcrumb.data || {}, {
                content: content.slice(0, 10),
              })
            }
            break
          }
        }

        return breadcrumb
      },
      /** 发送前过滤错误 */
      beforeSend(event) {
        const exception = event?.exception?.values[0] || {}
        switch (exception.type) {
          // 过滤接口状态码为200的没有catch的rejection，因为reject的不是Error，可以采用这种方式过滤
          // 如果是我们自己写的promise，出现没有catch的reject一般都是代码出错会抛出Error
          case 'UnhandledRejection':
            if (exception.value.indexOf('Non-Error promise rejection') !== -1) return null
            break
          case 'Error':
            // 过滤 Google Analytics 等第三方库抛出的非Error类型的错误
            if (exception.value.indexOf('Non-Error exception') !== -1) return null
            break
          default:
            break
        }

        return event
      },
    })
  } catch (exception) {
    // eslint-disable-next-line no-console
    console?.error(exception)
  }
}

/** 用于手动上报错误 */
export const sentryReport = (messageOrError: Error | string) => {
  const error = messageOrError instanceof Error ? messageOrError : new Error(messageOrError)
  Sentry.captureException(error, (scope) => {
    scope.setTag('errorType', 'scriptReport')
    scope.addBreadcrumb({
      type: 'error',
      level: Sentry.Severity.Error,
      category: 'stack',
      message: error.stack.slice(0, 300),
    })
    return scope
  })
}
